import { ApiError } from '../identity/types';
import type {
  ReviewStateWithholdingElectionInput,
  StateCertificateEntry,
  StateWithholdingCatalogResponse,
  StateWithholdingElectionDetail,
  StateWithholdingElectionsResponse,
  StateWithholdingStateView,
  StateWithholdingSubmissionDetail,
  SubmitStateWithholdingElectionInput,
} from './types';
import { acceptsElections } from './stateElections';

// EPN-52 mock mode: a synthetic slice of the state withholding certificate catalog and an in-memory store that follows the PR 2 API's rules (one open
// submission per state, maker-checker, catalog-checked answers, append-only elections). The catalog rows are copied from the Employees catalog file
// (version 2026-11-02-1) for the states the mock employees use; it is not the authority. Synthetic data only.

const base = {
  notEstablishedReason: null,
  filingStatuses: [],
  allowsExempt: false,
  allowsAdditionalWithholding: false,
  allowsExemptions: false,
  allowsExemptionAmount: false,
  allowsDependents: false,
  allowsLowerRateElection: false,
  allowsElectedRate: false,
  electedRates: [],
  defaultElectedRate: null,
  allowsSchoolDistrictNumber: false,
  smeQuestions: [],
  approvalStatus: 'PendingSmeReview',
} satisfies Omit<StateCertificateEntry, 'code' | 'status'>;

const bracket = { allowsExempt: true, allowsAdditionalWithholding: true, allowsExemptions: true, smeQuestions: ['SME-41'] };

export const MOCK_CATALOG: StateWithholdingCatalogResponse = {
  version: '2026-11-02-1',
  status: 'DraftPendingSmeApproval',
  states: [
    { ...base, code: 'AZ', status: 'NoCertificateRequired', allowsExempt: true, allowsElectedRate: true, electedRates: [0.005, 0.01, 0.015, 0.02, 0.025, 0.03, 0.035], defaultElectedRate: 0.02, smeQuestions: ['SME-41'] },
    { ...base, ...bracket, code: 'CA', status: 'NotEstablished', notEstablishedReason: 'SingleTableConfirmationPending', filingStatuses: [{ key: 'single', label: 'Single' }], smeQuestions: ['SME-41', 'SME-45'] },
    { ...base, code: 'IL', status: 'NoCertificateRequired', smeQuestions: ['SME-42'] },
    {
      ...base, code: 'MS', status: 'CertificateRequired', allowsExempt: true, allowsAdditionalWithholding: true, allowsExemptionAmount: true, smeQuestions: ['SME-41'],
      filingStatuses: [{ key: 'single', label: 'Single' }, { key: 'marriedFilingJointly', label: 'Married filing jointly' }, { key: 'headOfHousehold', label: 'Head of household' }],
    },
    {
      ...base, ...bracket, code: 'NJ', status: 'CertificateRequired',
      filingStatuses: ['A', 'B', 'C', 'D', 'E'].map((t) => ({ key: `rateTable${t}`, label: `Rate table ${t}` })),
    },
    { ...base, ...bracket, code: 'NY', status: 'CertificateRequired', filingStatuses: [{ key: 'singleOrHeadOfHousehold', label: 'Single or head of household' }, { key: 'married', label: 'Married' }] },
    { ...base, ...bracket, code: 'OH', status: 'EmptyCertificateAccepted', smeQuestions: ['SME-41', 'SME-44'] },
    { ...base, code: 'PA', status: 'NoCertificateRequired', smeQuestions: ['SME-42'] },
    { ...base, code: 'TX', status: 'NotApplicable' },
  ],
};

const today = () => new Date().toISOString().slice(0, 10);
const bad = (detail: string, code = 'STATE_ELECTION_INVALID') => new ApiError(400, detail, null, true, code, { code, detail });
const conflict = (detail: string, code: string) => new ApiError(409, detail, null, true, code, { code, detail });

interface Store {
  submissions: StateWithholdingSubmissionDetail[];
  elections: StateWithholdingElectionDetail[];
}

export class MockStateElectionStore {
  private stores = new Map<string, Store>();
  /** Who the mock thinks is signed in; the seeded pending submission was entered by someone else so it can be reviewed. */
  currentUserId = 'mock-admin';

  reset(): void {
    this.stores.clear();
    this.currentUserId = 'mock-admin';
  }

  private storeFor(employeeId: string): Store {
    let s = this.stores.get(employeeId);
    if (!s) {
      s = { submissions: [], elections: [] };
      if (employeeId === 'e1') {
        // A pending New Jersey submission entered by another administrator, so the review panel has something to show.
        s.submissions.push({
          submissionId: 'sub-nj-1', stateCode: 'NJ', effectiveFrom: '2026-01-01', source: 'Admin', status: 'SubmittedPendingReview', catalogVersion: MOCK_CATALOG.version,
          answers: { filingStatusKey: 'rateTableA', exemptions: 1, additionalWithholding: null, isExempt: false, electedRate: null, exemptionAmount: null, dependents: null, lowerRateElection: null, schoolDistrictNumber: null },
          submittedByUserId: 'mock-other-admin', submittedAtUtc: '2026-10-30T15:00:00Z', reviewedByUserId: null, reviewedAtUtc: null, electionId: null, concurrencyVersion: 1,
        });
      }
      this.stores.set(employeeId, s);
    }
    return s;
  }

  get(employeeId: string): StateWithholdingElectionsResponse {
    const s = this.storeFor(employeeId);
    const t = today();
    const codes = [...new Set([...s.elections.map((e) => e.stateCode), ...s.submissions.map((x) => x.stateCode)])].sort();
    const states: StateWithholdingStateView[] = codes.map((code) => {
      const forState = s.elections.filter((e) => e.stateCode === code);
      const current = forState.filter((e) => e.effectiveFrom <= t).sort((a, b) => b.effectiveFrom.localeCompare(a.effectiveFrom) || b.sequence - a.sequence)[0] ?? null;
      const upcoming = forState.filter((e) => e.effectiveFrom > t).sort((a, b) => a.effectiveFrom.localeCompare(b.effectiveFrom))[0] ?? null;
      const subs = s.submissions.filter((x) => x.stateCode === code).sort((a, b) => b.submittedAtUtc.localeCompare(a.submittedAtUtc));
      return {
        stateCode: code,
        catalogStatus: MOCK_CATALOG.states.find((c) => c.code === code)?.status ?? 'UnknownState',
        current,
        upcoming,
        openSubmission: subs.find((x) => x.status === 'SubmittedPendingReview') ?? null,
        history: subs.map(({ submissionId, stateCode, effectiveFrom, source, status, submittedByUserId, submittedAtUtc, reviewedByUserId, reviewedAtUtc }) => ({
          submissionId, stateCode, effectiveFrom, source, status, submittedByUserId, submittedAtUtc, reviewedByUserId, reviewedAtUtc,
        })),
      };
    });
    return { employeeId, catalogVersion: MOCK_CATALOG.version, states };
  }

  submit(employeeId: string, input: SubmitStateWithholdingElectionInput, source: 'Admin' | 'Employee' = 'Admin'): StateWithholdingSubmissionDetail {
    const s = this.storeFor(employeeId);
    const entry = MOCK_CATALOG.states.find((c) => c.code === input.stateCode);
    if (!entry) throw bad('That state is not one Escochex can record a withholding election for.');
    if (!acceptsElections(entry)) throw bad("That state's withholding needs no employee election.", 'STATE_ELECTION_NOT_ACCEPTED');
    if (!input.effectiveFrom) throw bad('Please enter the date the election takes effect.');
    if (source === 'Employee' && input.effectiveFrom < today()) throw bad('Your election can start today or later. Ask your payroll administrator if it must start earlier.');
    if (input.effectiveFrom < today() && !input.backdateReason) throw bad('Please give a reason for an election that takes effect before today.');
    if (!input.isExempt && entry.status === 'CertificateRequired' && !input.filingStatusKey) throw bad('Please choose a filing status.');
    if (s.submissions.some((x) => x.stateCode === input.stateCode && x.status === 'SubmittedPendingReview')) {
      throw conflict(
        source === 'Employee'
          ? 'You already have a submission for this state waiting for review. Withdraw it first to make a change.'
          : 'This employee already has a submission for this state waiting for review. Review or withdraw it first.',
        'STATE_ELECTION_SUBMISSION_OPEN',
      );
    }
    const row: StateWithholdingSubmissionDetail = {
      submissionId: `sub-${input.stateCode.toLowerCase()}-${s.submissions.length + 1}`,
      stateCode: input.stateCode,
      effectiveFrom: input.effectiveFrom,
      source,
      status: 'SubmittedPendingReview',
      catalogVersion: MOCK_CATALOG.version,
      answers: {
        filingStatusKey: input.filingStatusKey ?? null, exemptions: input.exemptions ?? null, additionalWithholding: input.additionalWithholding ?? null,
        isExempt: input.isExempt ?? false, electedRate: input.electedRate ?? null, exemptionAmount: input.exemptionAmount ?? null, dependents: input.dependents ?? null,
        lowerRateElection: input.lowerRateElection ?? null, schoolDistrictNumber: null,
      },
      submittedByUserId: this.currentUserId,
      submittedAtUtc: new Date().toISOString(),
      reviewedByUserId: null,
      reviewedAtUtc: null,
      electionId: null,
      concurrencyVersion: 1,
    };
    s.submissions.push(row);
    return row;
  }

  review(employeeId: string, submissionId: string, input: ReviewStateWithholdingElectionInput): StateWithholdingSubmissionDetail {
    const s = this.storeFor(employeeId);
    const row = s.submissions.find((x) => x.submissionId === submissionId);
    if (!row) throw new ApiError(404, 'Not found.', null, true, null, null);
    if (row.status !== 'SubmittedPendingReview') throw conflict('This submission has already been reviewed or withdrawn.', 'STATE_ELECTION_SUBMISSION_NOT_PENDING');
    if (row.concurrencyVersion !== input.concurrencyVersion) throw conflict('The submission changed since it was read. Reload it and try again.', 'CONCURRENCY_CONFLICT');
    if (row.submittedByUserId === this.currentUserId) {
      throw new ApiError(403, 'You cannot review a submission you entered. Another reviewer must review it.', null, true, 'SELF_APPROVAL_NOT_ALLOWED', null);
    }
    if (input.decision === 'Reject' && !input.note) throw bad('Please give a reason for rejecting the submission.');
    if (input.expiresOn && !row.answers.isExempt) throw bad('An expiry date can only be recorded for an exempt election, and must be after the date it takes effect.');
    const now = new Date().toISOString();
    let electionId: string | null = null;
    if (input.decision === 'Approve') {
      electionId = `el-${submissionId}`;
      const sequence = s.elections.filter((e) => e.stateCode === row.stateCode && e.effectiveFrom === row.effectiveFrom).length + 1;
      s.elections.push({
        electionId, stateCode: row.stateCode, effectiveFrom: row.effectiveFrom, sequence, answers: row.answers, expiresOn: input.expiresOn ?? null,
        catalogVersion: row.catalogVersion, submissionId, approvedByUserId: this.currentUserId, approvedAtUtc: now,
      });
    }
    const updated: StateWithholdingSubmissionDetail = {
      ...row, status: input.decision === 'Approve' ? 'Approved' : 'Rejected', reviewedByUserId: this.currentUserId, reviewedAtUtc: now, electionId,
      concurrencyVersion: row.concurrencyVersion + 1,
    };
    s.submissions = s.submissions.map((x) => (x.submissionId === submissionId ? updated : x));
    return updated;
  }

  /** The employee's own open submission for a state (EPN-52 PR 5); one an administrator entered is not theirs to withdraw. */
  withdrawOwn(employeeId: string, stateCode: string, userId: string): StateWithholdingSubmissionDetail {
    const s = this.storeFor(employeeId);
    const row = s.submissions.find((x) => x.stateCode === stateCode && x.status === 'SubmittedPendingReview' && x.source === 'Employee' && x.submittedByUserId === userId);
    if (!row) throw new ApiError(404, 'Not found.', null, true, null, null);
    return this.withdraw(employeeId, row.submissionId);
  }

  withdraw(employeeId: string, submissionId: string): StateWithholdingSubmissionDetail {
    const s = this.storeFor(employeeId);
    const row = s.submissions.find((x) => x.submissionId === submissionId);
    if (!row) throw new ApiError(404, 'Not found.', null, true, null, null);
    if (row.status !== 'SubmittedPendingReview') throw conflict('This submission has already been reviewed or withdrawn.', 'STATE_ELECTION_SUBMISSION_NOT_PENDING');
    const updated: StateWithholdingSubmissionDetail = { ...row, status: 'Withdrawn', concurrencyVersion: row.concurrencyVersion + 1 };
    s.submissions = s.submissions.map((x) => (x.submissionId === submissionId ? updated : x));
    return updated;
  }
}
